import { useState } from 'react'
import {
  Box,
  Table,
  Text,
  Badge,
  IconButton,
  HStack,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import { Eye, Trash2, CheckCircle, MoreVertical, Plus, Vote } from 'lucide-react'
import { useBudgets, useDeleteBudget, useVoteBudget } from '../hooks'
import { BudgetLineModal } from './BudgetLineModal'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import type { BudgetStatus, Budget } from '../types'
import { BUDGET_TYPE_LABELS } from '../types'

interface BudgetTableProps {
  residenceId: string
  fiscalYearId?: string
  selectedBudgetId?: string | null
  onBudgetClick?: (budgetId: string) => void
}

export function BudgetTable({
  residenceId,
  fiscalYearId,
  selectedBudgetId,
  onBudgetClick,
}: BudgetTableProps) {
  const { data: budgets, isLoading } = useBudgets(residenceId, fiscalYearId)
  const deleteBudget = useDeleteBudget()
  const voteBudget = useVoteBudget()

  // State for BudgetLineModal
  const [budgetLineModalOpen, setBudgetLineModalOpen] = useState(false)
  const [selectedBudgetForLine, setSelectedBudgetForLine] = useState<string | null>(null)

  // State for confirmation dialogs
  const [budgetToVote, setBudgetToVote] = useState<Budget | null>(null)
  const [budgetToDelete, setBudgetToDelete] = useState<Budget | null>(null)

  const getStatusBadge = (status: BudgetStatus) => {
    const config = {
      draft: { color: 'gray', label: 'Brouillon' },
      voted: { color: 'blue', label: 'Voté' },
      active: { color: 'green', label: 'Actif' },
      closed: { color: 'purple', label: 'Clôturé' },
    }
    const { color, label } = config[status]
    return (
      <Badge colorPalette={color} variant="subtle">
        {label}
      </Badge>
    )
  }

  const getBudgetTypeLabel = (type: string) => {
    return BUDGET_TYPE_LABELS[type as keyof typeof BUDGET_TYPE_LABELS] || type
  }

  const formatCurrency = (value: number | null) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const handleConfirmDelete = async () => {
    if (!budgetToDelete) return
    await deleteBudget.mutateAsync(budgetToDelete.id)
  }

  const handleConfirmVote = async () => {
    if (!budgetToVote) return
    await voteBudget.mutateAsync(budgetToVote.id)
  }

  const handleRowClick = (budgetId: string) => {
    onBudgetClick?.(budgetId)
  }

  if (isLoading) {
    return <Text>Chargement...</Text>
  }

  if (!budgets || budgets.length === 0) {
    return (
      <Box textAlign="center" py={8} color="gray.500">
        <Text>Aucun budget pour cet exercice</Text>
      </Box>
    )
  }

  return (
    <>
      <Box overflowX="auto">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Nom</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Exercice</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Montant total</Table.ColumnHeader>
              <Table.ColumnHeader>Statut</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
        <Table.Body>
          {budgets.map((budget) => (
            <Table.Row
              key={budget.id}
              data-budget-row
              cursor="pointer"
              onClick={() => handleRowClick(budget.id)}
              bg={selectedBudgetId === budget.id ? 'brand.50' : 'transparent'}
              _hover={{ bg: selectedBudgetId === budget.id ? 'brand.100' : 'gray.50' }}
              transition="background 0.15s"
            >
              <Table.Cell fontWeight="medium">{budget.name}</Table.Cell>
              <Table.Cell>{getBudgetTypeLabel(budget.budget_type)}</Table.Cell>
              <Table.Cell>{budget.fiscal_year?.name}</Table.Cell>
              <Table.Cell textAlign="right" fontWeight="semibold">
                {formatCurrency(budget.total_amount)}
              </Table.Cell>
              <Table.Cell>{getStatusBadge(budget.status)}</Table.Cell>
              <Table.Cell onClick={(e) => e.stopPropagation()}>
                <HStack justify="flex-end">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem
                            value="details"
                            onClick={() => onBudgetClick?.(budget.id)}
                          >
                            <HStack gap={2}>
                              <Eye size={16} />
                              <Text>Voir les détails</Text>
                            </HStack>
                          </MenuItem>
                          <MenuItem
                            value="add_line"
                            onClick={() => {
                              setSelectedBudgetForLine(budget.id)
                              setBudgetLineModalOpen(true)
                            }}
                          >
                            <HStack gap={2}>
                              <Plus size={16} />
                              <Text>Ajouter un poste</Text>
                            </HStack>
                          </MenuItem>
                          {budget.status === 'draft' && (
                            <>
                              <MenuItem
                                value="vote"
                                onClick={() => setBudgetToVote(budget)}
                              >
                                <HStack gap={2}>
                                  <CheckCircle size={16} />
                                  <Text>Marquer comme voté</Text>
                                </HStack>
                              </MenuItem>
                              <MenuItem
                                value="delete"
                                onClick={() => setBudgetToDelete(budget)}
                                color="red.600"
                                _hover={{ bg: 'red.50' }}
                              >
                                <HStack gap={2}>
                                  <Trash2 size={16} />
                                  <Text>Supprimer</Text>
                                </HStack>
                              </MenuItem>
                            </>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </HStack>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        </Table.Root>
      </Box>

      {/* Budget Line Modal */}
      {selectedBudgetForLine && (
        <BudgetLineModal
          isOpen={budgetLineModalOpen}
          onClose={() => {
            setBudgetLineModalOpen(false)
            setSelectedBudgetForLine(null)
          }}
          budgetId={selectedBudgetForLine}
          residenceId={residenceId}
        />
      )}

      {/* Vote Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!budgetToVote}
        onClose={() => setBudgetToVote(null)}
        onConfirm={handleConfirmVote}
        title="Marquer comme voté"
        message={`Confirmez-vous que le budget "${budgetToVote?.name}" a été voté en Assemblée Générale ?`}
        confirmLabel="Confirmer le vote"
        colorPalette="blue"
        icon={<Vote size={24} />}
        isLoading={voteBudget.isPending}
      />

      {/* Delete Confirmation Dialog */}
      {budgetToDelete && (
        <ConfirmDeleteDialog
          isOpen={!!budgetToDelete}
          onClose={() => setBudgetToDelete(null)}
          onConfirm={handleConfirmDelete}
          entityName={budgetToDelete.name}
          entityType="budget"
        />
      )}
    </>
  )
}
